import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import CheckoutApi from "api/checkout.api";
import { useDispatch } from "react-redux";
import { clearCart, getCartOther } from "redux/modules/cart";
import { GetOrdersByUser } from "redux/modules/order";
import { getCookie } from "config/cookies";
import Page from "components/Page";
import { CircularProgress } from "@mui/material";
import { PATHS } from "routes/paths";

const OmiseCallback = () => {
  const [status, setStatus] = useState("verifying");
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = getCookie("user") ? JSON.parse(getCookie("user")) : null;

  useEffect(() => {
    const verifyPayment = async () => {
      try {
        // First try to get transaction reference as recommended in workflow.yaml
        const urlParams = new URLSearchParams(location.search);
        const transactionRef = urlParams.get("ref");
        const chargeId = urlParams.get("id");

        if (!transactionRef && !chargeId) {
          setStatus("error");
          toast.error("Invalid payment response", {
            position: toast.POSITION.TOP_CENTER,
          });
          setTimeout(() => navigate(PATHS.cart.root), 3000);
          return;
        }

        // Use reference-based verification if available, otherwise fall back to charge ID
        let response;
        if (transactionRef) {
          response = await CheckoutApi.verifyOmisePaymentByRef(transactionRef);
        } else {
          response = await CheckoutApi.verifyOmisePayment(chargeId);
        }

        // Handle verification response based on workflow.yaml
        if (response.status === "successful") {
          setStatus("success");
          toast.success("Payment successful!", {
            position: toast.POSITION.TOP_CENTER,
          });

          // Update cart and orders
          if (user) {
            dispatch(GetOrdersByUser(user?._id));
          }
          dispatch(clearCart());
          dispatch(getCartOther());

          // Redirect to success page after a short delay
        } else if (response.status === "pending" && response.requires_action) {
          // If additional authentication is needed
          window.location.href = response.charge.authorize_uri;
        } else {
          setStatus("failed");
          toast.error(response.message || "Payment verification failed", {
            position: toast.POSITION.TOP_CENTER,
          });
          setTimeout(() => navigate(PATHS.cart.root), 3000);
        }
      } catch (error) {
        console.error("Payment verification error:", error);
        setStatus("error");
        toast.error("An error occurred during payment verification", {
          position: toast.POSITION.TOP_CENTER,
        });
        setTimeout(() => navigate(PATHS.cart.root), 3000);
      }
    };

    verifyPayment();
  }, [location, navigate, dispatch, user]);

  return (
    <Page title="Verifying Payment">
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "40vh",
          padding: "20px",
          textAlign: "center",
        }}
      >
        {status === "verifying" && (
          <>
            <CircularProgress size={60} />
            <h2 style={{ marginTop: "20px", color: "#5d5365" }}>
              Verifying your payment...
            </h2>
            <p style={{ color: "#666", marginTop: "10px" }}>
              Please wait while we confirm your transaction with Omise.
            </p>
          </>
        )}

        {status === "success" && (
          <>
            <div style={{ fontSize: "60px", color: "#4CAF50" }}>✓</div>
            <h2 style={{ marginTop: "20px", color: "#4CAF50" }}>
              Payment Successful!
            </h2>
            <p style={{ color: "#666", marginTop: "10px" }}>
              Your order has been received and is being processed.
            </p>
            <p style={{ color: "#666", marginTop: "5px" }}>
              You will be redirected shortly...
            </p>
          </>
        )}

        {status === "failed" && (
          <>
            <div style={{ fontSize: "60px", color: "#F44336" }}>✗</div>
            <h2 style={{ marginTop: "20px", color: "#F44336" }}>
              Payment Failed
            </h2>
            <p style={{ color: "#666", marginTop: "10px" }}>
              We couldn't complete your payment. Please try again.
            </p>
            <p style={{ color: "#666", marginTop: "5px" }}>
              You will be redirected to your cart shortly...
            </p>
          </>
        )}

        {status === "error" && (
          <>
            <div style={{ fontSize: "60px", color: "#FF9800" }}>!</div>
            <h2 style={{ marginTop: "20px", color: "#FF9800" }}>
              Verification Error
            </h2>
            <p style={{ color: "#666", marginTop: "10px" }}>
              We encountered an error while verifying your payment.
            </p>
            <p style={{ color: "#666", marginTop: "5px" }}>
              You will be redirected to your cart shortly...
            </p>
          </>
        )}
      </div>
    </Page>
  );
};

export default OmiseCallback;
